import { createContext, type ReactNode } from "react";
import { defaultError } from "@-/com.npmjs.react-context-default-error";

type HostListener = (node: ReactNode) => void;

/**
 * Sentinel a callsite publishes via `<PrimaryControlsStripExpansion hidden />` to suppress whatever
 * a lower-priority publisher (e.g. an enclosing layout) registered. The registry treats it
 * as "no content": `getCurrent` returns null and `hasContent` returns false, so the strip
 * stays empty and the expand/collapse toggle hides — while still being the most-recent entry,
 * so it wins over the layout's content for as long as the screen is focused.
 */
export const PRIMARY_CONTROLS_STRIP_EXPANSION_HIDDEN = Symbol(
  "primary-controls-strip-expansion-hidden",
);

export type PrimaryControlsStripExpansionEntry =
  | ReactNode
  | typeof PRIMARY_CONTROLS_STRIP_EXPANSION_HIDDEN;

export type PrimaryControlsStripExpansionPublishHandle = {
  unpublish: () => void;
  update: (node: PrimaryControlsStripExpansionEntry) => void;
};

/**
 * Tracks only the published expansion *content*. Whether the strip is expanded/collapsed is no
 * longer owned here — it lives in the app context (above the navigator) so it survives navigation.
 * See `usePrimaryControlsStripExpansionState`.
 */
export type PrimaryControlsStripExpansionRegistry = {
  getCurrent: () => ReactNode;
  hasContent: () => boolean;
  publish: (node: PrimaryControlsStripExpansionEntry) => PrimaryControlsStripExpansionPublishHandle;
  subscribe: (listener: HostListener) => () => void;
};

export function createPrimaryControlsStripExpansionRegistry(): PrimaryControlsStripExpansionRegistry {
  const entries = new Map<number, PrimaryControlsStripExpansionEntry>();
  let nextId = 0;
  const listeners = new Set<HostListener>();

  // The most-recently published entry wins. A `hidden` sentinel as the winner means the
  // focused screen has explicitly suppressed the strip, so both the content and the toggle
  // are treated as absent even though a lower-priority entry may still be registered.
  const getLast = (): PrimaryControlsStripExpansionEntry | undefined => {
    let last: PrimaryControlsStripExpansionEntry | undefined;
    for (const node of entries.values()) {
      last = node;
    }
    return last;
  };

  const getCurrent = (): ReactNode => {
    const last = getLast();
    if (last === undefined || last === PRIMARY_CONTROLS_STRIP_EXPANSION_HIDDEN) {
      return null;
    }
    return last;
  };

  const notify = () => {
    const current = getCurrent();
    for (const listener of listeners) {
      listener(current);
    }
  };

  const hasContent = () => {
    const last = getLast();
    return last !== undefined && last !== PRIMARY_CONTROLS_STRIP_EXPANSION_HIDDEN;
  };

  return {
    getCurrent,
    hasContent,
    publish(node) {
      const id = ++nextId;
      entries.set(id, node);
      notify();
      return {
        unpublish: () => {
          if (entries.delete(id)) {
            notify();
          }
        },
        update: (nextNode) => {
          if (entries.has(id)) {
            entries.set(id, nextNode);
            notify();
          }
        },
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const PrimaryControlsStripExpansionContext = createContext(
  defaultError<PrimaryControlsStripExpansionRegistry>("PrimaryControlsStripExpansionContext"),
);
